/*
 *
 *  * Copyright (c) 2018-2019 AccelByte Inc. All Rights Reserved.
 *  * This is licensed software from AccelByte Inc, for limitations
 *  * and restrictions contact your company contract manager.
 *
 */

import { default as React, lazy } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { Helmet } from "react-helmet";
import Toast from "./components/common/Toast/Toast";
import { ToastContext } from "./Providers/ToastProvider";
import { i18nInstance, t } from "./utils/i18n/i18n";
import history from "./utils/history";

const NotFoundPage = lazy(() => import("./components/error-pages/NotFoundPage/NotFoundPage"));
const InvalidRequestPage = lazy(() => import("./components/error-pages/InvalidRequestPage/InvalidRequestPage"));
const ServerIssuesPage = lazy(() => import("./components/error-pages/ServerIssuesPage/ServerIssuesPage"));
const LoginPage = lazy(() => import("./components/LoginPage/LoginPage"));
const RegisterPage = lazy(() => import("./components/RegisterPage/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("./components/ForgotPasswordPage/ForgotPasswordPage"));
const AlreadyAuthenticatedPage = lazy(() =>
  import("./components/error-pages/AlreadyAuthenticatedPage/AlreadyAuthenticatedPage")
);
const InvitationPage = lazy(() => import("./components/InvitationPage/InvitationPage"));

interface Props {}

interface State {
  initialized: boolean;
}

const validPathRegex = new RegExp("^\\/?([A-z0-9\\-_]+\\/)*([A-z0-9\\-_]+|)$");

class App extends React.Component<Props, State> {
  static contextType = ToastContext;
  context: React.ContextType<typeof ToastContext>;
  constructor(props: Props) {
    super(props);
    this.state = {
      initialized: false,
    };
  }

  async componentDidMount(): Promise<void> {
    await Promise.all([
      await import("./app-tasks")
        .then((module) => module.default)
        .then((taskRunner) => taskRunner()),
    ]).then(() => this.setState({ initialized: true }));
    i18nInstance.on("languageChanged", () => this.setState({}));

    this.redirectToTargetPath();
  }

  redirectToTargetPath = () => {
    const searchParam = new URLSearchParams(window.location.search);
    const targetPath = searchParam.get("target_auth_page");
    if (!targetPath) return;

    if (!validPathRegex.test(targetPath)) {
      history.replace("/error/400");
      return;
    }
    searchParam.delete("target_auth_page");
    history.replace(`${targetPath}?${searchParam.toString()}`);
  };

  render() {
    const { message, isDisplayed, type } = this.context;
    const modifyDocumentHead = () => {
      return (
        // @ts-ignore next-line
        <Helmet>
          <title>{t("App.title")}</title>
        </Helmet>
      );
    };
    return (
      <div className="App">
        {modifyDocumentHead()}
        <Toast type={type} message={message} isDisplayed={isDisplayed} />
        <Switch>
          <Route exact={true} path="/error/404" component={NotFoundPage} />
          <Route exact={true} path="/error/400" component={InvalidRequestPage} />
          <Route exact={true} path="/error/500" component={ServerIssuesPage} />
          <Route exact={true} path="/error/already-authenticated" component={AlreadyAuthenticatedPage} />
          <Route exact={true} path="/invitation/:invitationId" component={InvitationPage} />
          <Route exact={true} path="/register" component={RegisterPage} />
          <Route exact={true} path="/forgot-password" component={ForgotPasswordPage} />
          <Route exact={true} path="/login" component={LoginPage} />
          <Route
            exact={true}
            path="/"
            render={(props) => (
              <Redirect
                to={{
                  pathname: "/login",
                  search: props.location && props.location.search,
                }}
              />
            )}
          />
          <Redirect to="/error/404" />
        </Switch>
      </div>
    );
  }
}

export default App;
